import React, { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { CreatePetAndCheckupFormValues, useCreatePetAndCheckup } from '../../hooks/useSavePetName';
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  QueryLoaderWrapper,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text
} from '@companion-professional/components';
import { CheckupTag } from '@companion-professional/dctypes';
import { useAuthStore } from '../../state/auth';
import { useCheckupUrl } from '../../hooks/useCheckupUrl';
import { CheckupData } from './CreateQuickCheckup';

const SPECIES_OPTIONS = [
  { label: 'Canine', value: 'canine' },
  { label: 'Feline', value: 'feline' }
];

interface AddPetNameProps {
  // pendingFormValues holds the clinic pet id and location tag from step 1, needed to create the checkup.
  pendingFormValues: { clinicPetId: string; locationTag: CheckupTag };
  setCheckupData: (checkupData: CheckupData) => void;
}

// AddPetName is the second step of the Quick Checkup flow, shown when the entered clinic pet id was not found in the
// system. It collects the pet's name and species to create a new pet entry and checkup request in one step.
export const AddPetName: FunctionComponent<AddPetNameProps> = ({ pendingFormValues, setCheckupData }) => {
  const { isPending, error, createPetAndCheckup } = useCreatePetAndCheckup();
  const { getCheckupUrl } = useCheckupUrl();

  const { register, handleSubmit, reset, formState, setValue, watch } =
    useForm<Omit<CreatePetAndCheckupFormValues, 'clinicId' | 'clinicPetId' | 'locationTag'>>();
  const species = watch('species');

  return (
    <form
      onSubmit={handleSubmit((values) => {
        const clinicId = useAuthStore.getState().parsedToken?.clinic_id;
        if (!clinicId) {
          throw new Error('No clinic defined in the user token');
        }
        createPetAndCheckup({
          clinicId,
          clinicPetId: pendingFormValues.clinicPetId,
          locationTag: pendingFormValues.locationTag,
          petName: values.petName,
          species: values.species
        })
          .then((res) => {
            const checkupUrl = getCheckupUrl(res.id);
            if (checkupUrl) {
              setCheckupData({
                clinicPetId: pendingFormValues.clinicPetId,
                checkupId: res.id,
                checkupUrl
              });
            }
            reset();
          })
          .catch((err) => {
            console.log(err);
          });
      })}
    >
      <DialogHeader>
        <DialogTitle>Save New Pet Name</DialogTitle>
        <DialogDescription>
          The pet id you entered is not in our system. Please enter the pet's name and species to create a new pet
          entry.
        </DialogDescription>
      </DialogHeader>
      <QueryLoaderWrapper isPending={isPending} error={error} showChildrenOnError>
        <div className="mb-8 mt-4 flex flex-col space-y-5">
          <div className="flex flex-col space-y-1">
            <Label htmlFor="petName">Pet Name</Label>
            <Input
              id="petName"
              aria-invalid={formState.errors.petName ? 'true' : 'false'}
              {...register('petName', { required: true })}
            />
            {formState.errors.petName && formState.errors.petName.type === 'required' ? (
              // TODO: This should be it's own <InputError /> component
              <Text as="span" tone="destructive">
                The Pet Name is required.
              </Text>
            ) : null}
          </div>
          <div className="flex flex-col space-y-1">
            <input type="hidden" {...register('species', { required: true })} />
            <Label htmlFor="species">Species</Label>
            <Select value={species} onValueChange={(value) => setValue('species', value, { shouldValidate: true })}>
              <SelectTrigger id="species" className="border border-input">
                <SelectValue placeholder="Select species..." />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {SPECIES_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {formState.errors.species && formState.errors.species.type === 'required' ? (
              <Text as="span" tone="destructive">
                The Species is required.
              </Text>
            ) : null}
          </div>
        </div>
      </QueryLoaderWrapper>
      <DialogFooter className="flex flex-row items-end space-x-2 md:items-center">
        <Button size="small" type="submit" loading={isPending}>
          {isPending ? 'Saving...' : 'Save Pet'}
        </Button>
      </DialogFooter>
    </form>
  );
};
